import { Form, message, theme } from 'antd'
import { allTrim, handleError } from '../../../lib/utils'
import PatientExaminationForm from '../../../Bookings/components/ExaminationWrapper'
import CoreEnums from '../../../../core/utils/enums'
import coreReducer from '../../../../core/utils/reducer'
import { createBooking } from '../../../Bookings/utils/utils'
import { executeBooking, registerBooking } from '../../../Bookings/utils/bpm-utils'
import store from '../../../../store'
import { BookingDataTemplate } from '../../../Bookings/utils/booking-data-template'
import {
  MedPraxSchemePlanOptionSearch,
  MedPraxSchemePlanSearch,
  MedPraxSchemeSearch
} from '../../../Scripting/utils/utils'
import { debounce } from 'lodash'
import { firebaseAuth } from '../../../..'
import { PhoneAuthProvider, RecaptchaVerifier, updatePhoneNumber } from 'firebase/auth'
import { getCountries, updateAgiliteUser } from '../../../Auth/utils/utils'
import { useCallback, useEffect, useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import systemUsersReducer from '../utils/reducer'
import dayjs from 'dayjs'

const useEditPatient = ({ dataId, refreshView, isExamination, handleRootTabKey }) => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const activeRecords = useSelector((state) => state.systemUsers.activeRecords)
  const dataIndex = activeRecords.findIndex((item) => item._id === dataId)

  const [examinationModalOpen, setExaminationModalOpen] = useState(false)
  const userData = activeRecords[dataIndex]
  const authState = useSelector((state) => state.auth)
  const [loading, setLoading] = useState(false)
  const [confirmationOpen, setConfirmationOpen] = useState(false)
  const [confirmationCode, setConfirmationCode] = useState(null)
  // eslint-disable-next-line
  const [verificationId, setVerificationId] = useState(null)
  const [submitDisabled, setSubmitDisabled] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [specialFormState, setSpecialFormState] = useState([
    { key: 'firstName', value: userData.firstName },
    { key: 'lastName', value: userData.lastName }
  ])
  const [residentVisitor, setResidentVisitor] = useState(
    userData.country ? (userData?.country === 'ZA' ? 'resident' : 'visitor') : 'resident'
  )
  const [country, setCountry] = useState('ZA')
  const [countries, setCountries] = useState([])
  const [countriesLoading, setCountriesLoading] = useState(false)
  const [countryObject, setCountryObject] = useState()

  // Medical Aid
  const [schemeLoading, setSchemeLoading] = useState(false)
  const [schemeData, setSchemeData] = useState([])
  const [plansLoading, setPlansLoading] = useState(false)
  const [plansData, setPlansData] = useState([])
  const [planOptionsLoading, setPlanOptionsLoading] = useState(false)
  const [planOptionData, setPlanOptionData] = useState([])

  // Actions Modal Open States
  const [medicalHistoryModalOpen, setMedicalHistoryModalOpen] = useState(false)
  const [clinicalNotesModalOpen, setClinicalNotesModalOpen] = useState(false)
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false)
  const [referralModalOpen, setReferralModalOpen] = useState(false)
  const [followUpModalOpen, setFollowUpModalOpen] = useState(false)
  const [sickNoteModalOpen, setSickNoteModalOpen] = useState(false)
  const { token } = theme.useToken()

  // Add a useEffect hook to log userData when it changes
  useEffect(() => {
    if (userData) {
      // Always reset the form with the current userData
      const initialValues = {
        ...userData,
        phoneNumber: userData.phoneNumber ? userData.phoneNumber.replace('+27', '') : null,
        medicalAid: {
          name: userData.medicalAid?.name ? userData.medicalAid.name : undefined,
          schemeCode: userData.medicalAid?.schemeCode ? userData.medicalAid.schemeCode : undefined,
          plan: userData.medicalAid?.plan ? userData.medicalAid.plan : undefined,
          planCode: userData.medicalAid?.planCode ? userData.medicalAid.planCode : undefined,
          planOption: userData.medicalAid?.planOption ? userData.medicalAid.planOption : undefined,
          planOptionCode: userData.medicalAid?.planOptionCode ? userData.medicalAid.planOptionCode : undefined,
          number: userData.medicalAid?.number ? userData.medicalAid.number : '',
          dependantNumber: userData.medicalAid?.dependantNumber ? userData.medicalAid.dependantNumber : ''
        }
      }

      // Handle dateOfBirth specifically
      if (userData.dateOfBirth) {
        try {
          // Try to parse the date using dayjs
          const dateObj = dayjs(userData.dateOfBirth)
          if (dateObj.isValid()) {
            initialValues.dateOfBirth = dateObj
          } else {
            // If date is invalid, set it to null
            initialValues.dateOfBirth = null
          }
        } catch (error) {
          // If there's an error parsing the date, set it to null
          initialValues.dateOfBirth = null
        }
      }

      // Reset the form with all values
      form.setFieldsValue(initialValues)

      // If medical aid scheme is selected, fetch plans
      if (userData.medicalAid?.schemeCode) {
        handleGetMedicalAidPlans(userData.medicalAid.schemeCode)
      }

      // If plan is selected, fetch plan options
      if (userData.medicalAid?.planCode) {
        handleGetMedicalAidPlanOptions(userData.medicalAid.planCode)
      }
    }
  }, [userData, form])

  // Add a function to retrieve the date from localStorage if needed
  const getDateOfBirthFromStorage = () => {
    const storedDate = localStorage.getItem(`patient_dob_${dataId}`)
    return storedDate
  }

  // Add an additional useEffect to check localStorage as a fallback
  useEffect(() => {
    if (userData && !userData.dateOfBirth) {
      const storedDate = getDateOfBirthFromStorage()
      if (storedDate) {
        form.setFieldValue('dateOfBirth', dayjs(storedDate))
      }
    }
  }, [userData, form, dataId])

  useEffect(() => {
    let tmpIndex = -1

    tmpIndex = countries.findIndex((e) => e.code === country)

    if (tmpIndex > -1) {
      setCountryObject(countries[tmpIndex])
    } else {
      setCountryObject(null)
    }

    // eslint-disable-next-line
  }, [country])

  useEffect(() => {
    async function handleLoginPhoneRecaptcha() {
      window.recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
        size: 'invisible',
        callback: (response) => {
          setSubmitDisabled(false)
        },
        'expired-callback': (response) => {
          setSubmitDisabled(true)
          setErrorMessage('Verification Expired. Please reload the page')
        }
      })
    }

    handleLoginPhoneRecaptcha()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    handleGetCountries()
    window.scrollTo(0, 0)
    // eslint-disable-next-line
  }, [])

  const handleGetCountries = async () => {
    let tmpCountries = []

    setCountriesLoading(true)

    try {
      tmpCountries = await getCountries()
      setCountries(tmpCountries)
    } catch (e) {
      message.error(handleError(e, true))
    }

    setCountriesLoading(false)
  }

  const formatNumber = (phoneNumber) => {
    if (phoneNumber.charAt(0) === '0') {
      phoneNumber = `${countryObject ? countryObject.phoneCode : '+27'}${phoneNumber.slice(1, 10)}`
    } else {
      phoneNumber = `${countryObject ? countryObject.phoneCode : '+27'}${phoneNumber}`
    }

    return phoneNumber
  }

  const handleSubmit = async () => {
    const formData = allTrim(form.getFieldsValue())
    setLoading(true)

    try {
      if (!formData.phoneNumber && userData.phoneNumber) {
        setLoading(false)
        return message.error('You cannot remove patient cellphone number, please revise')
      }

      // Check if phone number has changed

      await handleSubmitExtended()
    } catch (e) {
      message.error(handleError(e, true))
    }
    setLoading(false)
  }

  const handleSubmitExtended = async () => {
    try {
      // Get the date of birth from the form
      const dateOfBirth = form.getFieldValue('dateOfBirth')

      // Format the date for storage - ensure it's in YYYY-MM-DD format
      let formattedDateOfBirth = null
      if (dateOfBirth) {
        if (typeof dateOfBirth === 'string') {
          // If it's already a string, try to parse and format it
          formattedDateOfBirth = dayjs(dateOfBirth).isValid() ? dayjs(dateOfBirth).format('YYYY-MM-DD') : null
        } else if (dateOfBirth.isValid && dateOfBirth.isValid()) {
          // If it's a dayjs object
          formattedDateOfBirth = dateOfBirth.format('YYYY-MM-DD')
        } else if (dateOfBirth instanceof Date) {
          // If it's a Date object
          formattedDateOfBirth = dayjs(dateOfBirth).format('YYYY-MM-DD')
        }
      }

      // Now get the rest of the form data
      const formData = allTrim(form.getFieldsValue())
      let phoneNumber = form.getFieldValue('phoneNumber')

      if (phoneNumber) {
        phoneNumber = formatNumber(phoneNumber)
        form.setFieldValue('phoneNumber', phoneNumber.replace(countryObject ? countryObject.phoneCode : '+27', ''))
      }

      // Properly handle the medical aid data to preserve all codes
      const medicalAidData = formData.medicalAid || {}
      const existingMedicalAid = userData.medicalAid || {}

      // Create a properly merged medical aid object that preserves all codes
      const mergedMedicalAid = {
        ...existingMedicalAid,
        ...medicalAidData,
        // Explicitly ensure codes are preserved
        schemeCode: medicalAidData.schemeCode || existingMedicalAid.schemeCode,
        planCode: medicalAidData.planCode || existingMedicalAid.planCode,
        planOptionCode: medicalAidData.planOptionCode || existingMedicalAid.planOptionCode
      }

      // Create a new object with all the data, explicitly setting dateOfBirth and medicalAid
      const updatedData = {
        ...userData,
        ...formData,
        phoneNumber,
        dateOfBirth: formattedDateOfBirth, // Explicitly set the formatted date
        medicalAid: mergedMedicalAid, // Use the properly merged medical aid data
        extraData: {
          ...userData.extraData,
          ...formData.extraData
        }
      }

      // Update the user record
      let newRecord = await updateAgiliteUser(userData._id, updatedData)

      // Store the date in localStorage for guaranteed persistence
      if (formattedDateOfBirth) {
        localStorage.setItem(`patient_dob_${dataId}`, formattedDateOfBirth)
      }

      // Force update the Redux store with the correct date
      const tmpActiveRecords = activeRecords.concat()
      const recordIndex = tmpActiveRecords.findIndex((item) => item._id === dataId)

      if (recordIndex !== -1) {
        // Create a new record with the explicit dateOfBirth
        const updatedRecord = {
          ...newRecord,
          dateOfBirth: formattedDateOfBirth
        }

        // Update the record in the active records array
        tmpActiveRecords[recordIndex] = { ...userData, ...updatedRecord }

        // Update the Redux store
        dispatch(systemUsersReducer.actions.setActiveRecords(tmpActiveRecords))
      }

      // Show success message
      message.success('Profile updated successfully.')

      // Handle navigation based on context
      if (isExamination) {
        refreshView('success', newRecord)
      } else {
        // Just refresh the data without closing the tab
        refreshView()
      }
    } catch (e) {
      message.error(handleError(e, true))
    }
  }

  const handleLoginPhoneCode = async () => {
    let phoneCredential = null

    setLoading(true)

    try {
      setConfirmationOpen(false)

      phoneCredential = PhoneAuthProvider.credential(verificationId, confirmationCode)
      await updatePhoneNumber(firebaseAuth.currentUser, phoneCredential)
      await handleSubmitExtended()
    } catch (e) {
      setConfirmationOpen(true)
      message.error(handleError(e, true))
    }

    setConfirmationCode('')
    setLoading(false)
  }

  const handleGetMedicalAids = async (query) => {
    let tmpData = []

    setSchemeLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemeSearch(query)
        setSchemeData(tmpData)
      } else {
        setSchemeData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setSchemeLoading(false)
  }

  const handleGetMedicalAidPlans = async (query) => {
    let tmpData = []

    setPlansLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemePlanSearch(query)
        setPlansData(tmpData)
      } else {
        setPlansData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setPlansLoading(false)
  }

  const handleGetMedicalAidPlanOptions = async (query) => {
    let tmpData = []

    setPlanOptionsLoading(true)

    try {
      if (query) {
        tmpData = await MedPraxSchemePlanOptionSearch(query)
        setPlanOptionData(tmpData)
      } else {
        setPlanOptionData([])
      }
    } catch (e) {
      message.error(handleError(e, true))
    }

    setPlanOptionsLoading(false)
  }

  // eslint-disable-next-line
  const debouncedSchemeFilter = useCallback(
    debounce((query) => {
      if (query) {
        handleGetMedicalAids(query)
      } else {
        setSchemeLoading(false)
      }
    }, 1000),
    []
  )

  const closeTab = () => {
    let tmpActiveRecords = activeRecords.concat()

    tmpActiveRecords = tmpActiveRecords.filter((i) => i._id !== dataId)

    // Call refreshView first to ensure the patients list is updated
    refreshView()

    batch(() => {
      // Update the active records in the Redux store
      dispatch(systemUsersReducer.actions.setActiveRecords(tmpActiveRecords))

      // Close the current tab
      dispatch(
        coreReducer.actions.closeTab({
          targetKey: `${handleRootTabKey()}_${dataId}`,
          removeBreadcrumb: true
        })
      )

      // Ensure the patients list tab is active
      dispatch(coreReducer.actions.changeTab(handleRootTabKey()))
    })
  }

  const beginAdhocScreening = async (data) => {
    // setLoading(true)
    const clinic = JSON.parse(data.clinic)
    let record = BookingDataTemplate(activeRecords[dataIndex], clinic, {
      chiefComplaint: data.ailment,
      ailment: data.ailment,
      patientSelection: data.patientSelection,
      patientInput: data.patientInput,
      paymentMethod: 'No Appointment',
      service: authState.agiliteUser.extraData.services[0],
      processRef: null,
      status: null
    })
    let bpmProcess = null
    let processUser = `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`

    try {
      bpmProcess = await registerBooking('patient_journey', processUser)

      if (store.getState().auth.agiliteUser.extraData.profession === 'doctor') {
        // New To Consultation (Skipping Checkin and Screening)
        bpmProcess = await executeBooking(
          'patient_journey',
          bpmProcess.recordId,
          'submit',
          processUser,
          bpmProcess.key,
          'consultation'
        )
      } else {
        // New To Screening (Skipping Checkin)
        bpmProcess = await executeBooking(
          'patient_journey',
          bpmProcess.recordId,
          'submit',
          processUser,
          bpmProcess.key,
          'screening'
        )
      }

      record.processRef = bpmProcess.recordId
      record.status = clinic.bpmConfig?.find((e) => e.key === bpmProcess.key)?.key

      record = { ...record, ...(await createBooking(record)) }

      dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.MY_BOOKINGS}_${record._id}`,
          closable: true,
          label: `Booking: ${record.patientName}`,
          children: <PatientExaminationForm data={record} noAppointment={true} />
        })
      )
      setExaminationModalOpen(false)
    } catch (e) {
      message.error(handleError(e, true))
    }
    // setLoading(false)
  }

  const handleModalReset = () => {
    setFollowUpModalOpen(false)
    setSickNoteModalOpen(false)
    setReferralModalOpen(false)
    setAssessmentModalOpen(false)
    setClinicalNotesModalOpen(false)
    setMedicalHistoryModalOpen(false)
  }
  return {
    beginAdhocScreening,
    closeTab,
    handleGetMedicalAidPlanOptions,
    handleGetMedicalAidPlans,
    handleLoginPhoneCode,
    handleModalReset,
    handleSubmit,
    setAssessmentModalOpen,
    setClinicalNotesModalOpen,
    setConfirmationCode,
    setConfirmationOpen,
    setCountry,
    setExaminationModalOpen,
    setFollowUpModalOpen,
    setMedicalHistoryModalOpen,
    setPlanOptionData,
    setPlansData,
    setReferralModalOpen,
    setResidentVisitor,
    setSchemeData,
    setSickNoteModalOpen,
    setSpecialFormState,
    assessmentModalOpen,
    authState,
    clinicalNotesModalOpen,
    confirmationCode,
    confirmationOpen,
    countries,
    countriesLoading,
    countryObject,
    dataIndex,
    debouncedSchemeFilter,
    errorMessage,
    examinationModalOpen,
    followUpModalOpen,
    form,
    loading,
    medicalHistoryModalOpen,
    planOptionData,
    planOptionsLoading,
    plansData,
    plansLoading,
    referralModalOpen,
    residentVisitor,
    schemeData,
    schemeLoading,
    sickNoteModalOpen,
    specialFormState,
    submitDisabled,
    token,
    userData
  }
}

export default useEditPatient
